import { useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '@marriott/mi-headless-utils';
import {
  normalizeHwsRlmCandidates,
  normalizeReslinkCandidates,
  resolveNowMs,
  selectHomepageRetargetingCandidate,
} from '../utils/retargeting/retargetingCandidates';
import type {
  HomepageRetargetingCandidate,
  HomepageRetargetingUseCase,
  UseRetargetingCandidateArgs,
} from '../utils/retargeting/retargeting.types';
import {
  HOMEPAGE_HWS_RLM_USE_CASE,
  RETARGETING_CANDIDATE_READER_ERROR_EVENT,
} from '../utils/retargeting/retargeting.constants';
import { readClientRlmSearchCriteria } from '../utils/retargeting/retargetingRlmPathing';
import {
  DATALAYER_LOADED_EVENT,
  DATALAYER_RECOVERY_FETCH_DELAY_MS,
  fetchDatalayerSnapshot,
} from '../utils/retargeting/retargetingDatalayerRecovery';
import {
  getLaunchConsent,
  getOneTrustAdvertisingConsent,
  getRegionalDefaultConsent,
  LAUNCH_OPTIN,
  LAUNCH_OPTOUT,
  RESLINK,
} from '@marriott/mi-localstorage-utils';

const { log } = logger({})('useRetargetingCandidate');

// WEB-215711: akmCC (Akamai country) is HttpOnly and unreadable via document.cookie
// client-side, so the shared gate's regional-default tier can never see it. The
// Akamai-derived country is mirrored in the data layer under this key.
const DATALAYER_AKAMAI_COUNTRY_KEY = 'browser_akamai_loc_country';

function getClientCountry(sessionDatalayer: Record<string, unknown> | undefined): string | null {
  const acdlCountry =
    typeof window !== 'undefined' ? window.adobeDataLayer?.getState?.()?.[DATALAYER_AKAMAI_COUNTRY_KEY] : undefined;
  const legacyCountry =
    typeof window !== 'undefined'
      ? (window as { dataLayer?: Record<string, unknown> }).dataLayer?.[DATALAYER_AKAMAI_COUNTRY_KEY]
      : undefined;
  const country = acdlCountry ?? legacyCountry ?? sessionDatalayer?.[DATALAYER_AKAMAI_COUNTRY_KEY];
  return typeof country === 'string' && country.length > 0 ? country : null;
}

// WEB-207389 fail-closed precedence, evaluated fresh each render:
// 1) Adobe Launch user_optin_status, 2) OneTrust advertising group, 3) regional default.
// WEB-215711: tier 3 uses the data-layer country (not akmCC) because the cookie is HttpOnly.
function canRetarget(sessionDatalayer: Record<string, unknown> | undefined): boolean {
  try {
    const launch = getLaunchConsent();
    if (launch === LAUNCH_OPTIN) return true;
    if (launch === LAUNCH_OPTOUT) return false;

    const oneTrust = getOneTrustAdvertisingConsent();
    if (oneTrust !== null) return oneTrust;

    return getRegionalDefaultConsent(getClientCountry(sessionDatalayer));
  } catch {
    return false;
  }
}

interface RetargetingResolution {
  candidate: HomepageRetargetingCandidate | null;
  /**
   * True when an HWS/RLM candidate was resolved without any search criteria —
   * the datalayer held no funnel data at read time, so the card fell back to
   * the generic flexible link. Late-arriving data can still enrich it.
   */
  needsCriteriaRecovery: boolean;
}

const EMPTY_RESOLUTION: RetargetingResolution = { candidate: null, needsCriteriaRecovery: false };

export function useRetargetingCandidate({
  now,
  eligibleUseCases,
  refreshKey,
  sessionDatalayer,
}: UseRetargetingCandidateArgs): HomepageRetargetingCandidate | null {
  const normalizedNow = resolveNowMs(now);
  const eligibleUseCasesKey = eligibleUseCases.join('|');
  const refreshKeyToken = refreshKey ?? null;
  // The session datalayer bootstrap can fail or lag for a whole page view
  // (empty ACDL state on first landing), leaving the RLM card on its generic
  // flexible fallback even though the guest's dated search exists server-side.
  // Recovery: re-read on the provider's dataLayerLoaded push, and after a
  // grace period re-fetch the datalayer once directly.
  const [recoveredDatalayer, setRecoveredDatalayer] = useState<Record<string, unknown> | null>(null);
  const [acdlRefreshTick, setAcdlRefreshTick] = useState(0);
  const recoveryFetchStartedRef = useRef(false);

  const resolution = useMemo<RetargetingResolution>(() => {
    try {
      void refreshKeyToken;
      void acdlRefreshTick;
      // WEB-207389 / WEB-215711: fail-closed consent gate. No read of retargeting
      // localStorage unless the visitor may be retargeted. Evaluated fresh each render.
      if (!canRetarget(sessionDatalayer)) return EMPTY_RESOLUTION;
      const normalizedEligibleUseCases = (
        eligibleUseCasesKey ? eligibleUseCasesKey.split('|') : []
      ) as HomepageRetargetingUseCase[];
      const eligibleUseCaseSet = new Set(normalizedEligibleUseCases);
      const candidates: HomepageRetargetingCandidate[] = [];
      if (eligibleUseCaseSet.has(HOMEPAGE_HWS_RLM_USE_CASE)) {
        candidates.push(...normalizeHwsRlmCandidates(normalizedNow));
      }
      if (eligibleUseCaseSet.has(RESLINK)) {
        candidates.push(...normalizeReslinkCandidates(normalizedNow));
      }

      const searchCriteria =
        readClientRlmSearchCriteria(sessionDatalayer) ??
        (recoveredDatalayer ? readClientRlmSearchCriteria(recoveredDatalayer) : null);

      return {
        candidate: selectHomepageRetargetingCandidate({
          now: normalizedNow,
          eligibleUseCases: normalizedEligibleUseCases,
          candidates,
          searchCriteria,
        }),
        // Only HWS/RLM pathing consumes criteria, so recovery is pointless for
        // ResLink-only candidates or once any criteria were readable.
        needsCriteriaRecovery:
          searchCriteria === null && candidates.some(candidate => candidate.useCase === HOMEPAGE_HWS_RLM_USE_CASE),
      };
    } catch (error) {
      log.warn(RETARGETING_CANDIDATE_READER_ERROR_EVENT, {
        errorType: error instanceof Error ? error.name : typeof error,
      });
      return EMPTY_RESOLUTION;
    }
  }, [normalizedNow, eligibleUseCasesKey, refreshKeyToken, sessionDatalayer, recoveredDatalayer, acdlRefreshTick]);

  const { needsCriteriaRecovery } = resolution;
  useEffect(() => {
    if (typeof window === 'undefined' || !needsCriteriaRecovery) return undefined;
    const onDatalayerLoaded = () => setAcdlRefreshTick(tick => tick + 1);
    const acdl = window.adobeDataLayer;
    const canListen = typeof acdl?.addEventListener === 'function';
    if (canListen) acdl.addEventListener(DATALAYER_LOADED_EVENT, onDatalayerLoaded);
    let cancelled = false;
    const fetchTimer = window.setTimeout(() => {
      if (recoveryFetchStartedRef.current) return;
      recoveryFetchStartedRef.current = true;
      void fetchDatalayerSnapshot().then(snapshot => {
        if (!cancelled && snapshot) setRecoveredDatalayer(snapshot);
      });
    }, DATALAYER_RECOVERY_FETCH_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(fetchTimer);
      if (canListen && typeof acdl?.removeEventListener === 'function') {
        acdl.removeEventListener(DATALAYER_LOADED_EVENT, onDatalayerLoaded);
      }
    };
  }, [needsCriteriaRecovery]);

  return resolution.candidate;
}
